import { derived, type Loadable } from '@square/svelte-store';
import { shopify } from '../shopify';
import { cartId, deleteCartId } from './cart-id';
import { get } from 'svelte/store';
import { identifyUserForCart } from '$lib/stores/user';
import type {
  AddToCartMutationVariables,
  CompleteCartFragment,
  CreateCartMutationVariables
} from '$gql/storefront/operations';
import { isGraphqlError } from '$gql/storefront/utils';
import { toaster } from '$lib/toaster';
import { t } from '$lib/i18n';
import { AnalyticsEvents, analyticsEvent } from '$lib/analytics';
import { cart, lastAddedItemId } from './cart';
import {
  campaignProgressLevelProduct,
  tieredCampaign,
  freeGiftCampaign
} from '$lib/stores/campaigns';
import { FREE_GIFT } from '$lib/constants';
import { selectedGift } from '$components/cart/change-gift.svelte';

// Create Cart
export const createCart = derived([shopify], ([$shopify]) => {
  return async (lines: CreateCartMutationVariables['lines'] | null) => {
    const errorTitle = lines ? get(t)('cart.errors.could-not-add') : get(t)('error.generic.title');
    const buyerIdentity = await identifyUserForCart();

    return $shopify
      .createCart({
        lines: lines ?? [],
        buyerIdentity
      })
      .then(({ cartCreate: data }) => {
        handleCartResolve(errorTitle, data);

        if (lines) {
          // set last added item id
          lastAddedItemId.set(Array.isArray(lines) ? lines[0].merchandiseId : lines.merchandiseId);
        }

        return data;
      })
      .catch(() => {
        handleCartError(errorTitle);
      });
  };
});

// add to cart
export const addToCart = derived(
  [shopify, cartId, createCart],
  ([$shopify, $cartId, $createCart]) => {
    return async (lines: AddToCartMutationVariables['lines']) => {
      if (!$cartId) return $createCart(lines);

      return $shopify
        .addToCart({ cartId: $cartId, lines })
        .then(async ({ cartLinesAdd: data }) => {
          handleCartResolve(get(t)('cart.errors.could-not-add'), data);

          // set last added item id
          lastAddedItemId.set(Array.isArray(lines) ? lines[0].merchandiseId : lines.merchandiseId);

          await handleAutoAddToCart(data?.cart);

          return data;
        })
        .catch((error) => {
          handleCartError(get(t)('cart.errors.could-not-add'), error);
        });
    };
  }
);

// update cart quantity
export const updateCartQuantity = derived([shopify, cartId, cart], ([$shopify, $cartId, $cart]) => {
  if (!$cartId)
    return async (_lineId: string, _quantity: number) => {
      console.error('cannot update cart quantity without a cart id');
    };

  // get current cart quantity to compare with the new one
  const cartQuantity = $cart?.totalQuantity || 0;

  return async (lineId: string, quantity: number, itemId: string) => {
    return $shopify
      .updateCartQuantity({
        cartId: $cartId,
        lineId,
        quantity
      })
      .then(async ({ cartLinesUpdate: data }) => {
        handleCartResolve(get(t)('cart.errors.could-not-update'), data);

        // set last added item id
        lastAddedItemId.set(itemId);

        // increasing cart
        if (data?.cart?.totalQuantity && data.cart.totalQuantity > cartQuantity) {
          await handleAutoAddToCart(data?.cart);
        }

        // decreasing cart
        if (data?.cart?.totalQuantity && data.cart.totalQuantity < cartQuantity) {
          await handleRemoveFreeGift(data?.cart);
        }

        // Trigger analytic event based on previous cart state to know which product was removed
        if (quantity === 0) {
          // remove cart from storage
          deleteCartId();

          const affectedProduct = $cart?.lines.nodes.filter((line) => line.id === lineId)[0];

          if (affectedProduct) {
            const variantTitle = affectedProduct.merchandise.selectedOptions
              .map((option) => option.value)
              .join(' / ');
            analyticsEvent({
              event: AnalyticsEvents.REMOVE_FROM_CART,
              currency: affectedProduct.cost.amountPerQuantity.currencyCode,
              value: +affectedProduct.cost.amountPerQuantity.amount,
              items: [
                {
                  item_id: affectedProduct.merchandise.product.handle,
                  item_name: affectedProduct.merchandise.product.title,
                  item_category: affectedProduct.merchandise.product.productType,
                  item_variant: variantTitle
                }
              ]
            });
          }

          return data;
        }

        return data;
      })
      .catch((error) => {
        handleCartError(get(t)('cart.errors.could-not-update'), error);
      });
  };
});

// add discount code to cart
export const addDiscountToCart = derived(
  [shopify, cartId, createCart],
  ([$shopify, $cartId, $createCart]) => {
    return async (discountCode: string | string[], isRemoved = false) => {
      let cartId: string = $cartId || '';
      const discountCodes = typeof discountCode === 'string' ? [discountCode] : discountCode;

      if (!$cartId) {
        const res = await $createCart(null);

        if (!res?.cart?.id) throw new Error('Failed to create cart');

        cartId = res.cart.id;
      }

      return $shopify
        .addDiscountCode({
          cartId,
          discountCodes
        })
        .then(async (data) => {
          if (!isRemoved) {
            await handleRemoveFreeGift(data.cartDiscountCodesUpdate?.cart);
          }

          await handleCartResolve(get(t)('error.generic.title'), data.cartDiscountCodesUpdate);

          if (isRemoved) {
            await handleAutoAddToCart(data.cartDiscountCodesUpdate?.cart);
          }

          toaster(
            {
              title: isRemoved ? get(t)('discount.remove.title') : get(t)('discount.toast.title'),
              description: get(t)('discount.toast.description')
            },
            { duration: 5000 }
          );

          return data;
        });
    };
  }
);

// UTILS
const handleCartResolve = async (
  errorTitle: string,
  data?: {
    cart?: CompleteCartFragment | null;
    userErrors?: {
      field?: string[] | null;
      message: string;
    }[];
  } | null
) => {
  if (data?.userErrors?.length) {
    // delete cart id if cart is corrupted
    if (data?.userErrors.some((e) => e.message.includes('cart does not exist.'))) {
      deleteCartId();
    }

    toaster({
      type: 'error',
      title: errorTitle,
      description: data.userErrors[0].message
    });

    return;
  }

  if (!data?.cart?.id) throw new Error('could not add to cart');

  // all ok
  await cart.set(data.cart);
};

const handleCartError = (errorTitle: string, error?: unknown) => {
  // delete cart id if cart is corrupted
  if (isGraphqlError(error)) {
    if (error[0].message === 'Variable $cartId of type ID! was provided invalid value') {
      deleteCartId();
    }
  }

  toaster({
    type: 'error',
    title: errorTitle,
    description: get(t)('common.try-again')
  });
};

// the admin can choose a product to be added automatically on certain cart progress level
const handleAutoAddToCart = async (cart: CompleteCartFragment | null | undefined) => {
  try {
    // no cart
    if (!(cart && cart?.id)) return null;

    const product = await campaignProgressLevelProduct.load();

    // not available for sale
    if (!product || !product?.id || !product.availableForSale) return null;

    // already in cart
    if (cart?.lines.nodes.some((line) => line.merchandise.product.id === product?.id)) return null;

    // get the first variant available for sale
    const variantId =
      product.variants.nodes.find((variant) => variant.availableForSale)?.id ?? null;

    if (variantId) {
      await get(addToCart)([
        {
          merchandiseId: variantId,
          quantity: 1,
          attributes: [{ key: FREE_GIFT, value: 'true' }]
        }
      ]);
    }
  } catch (error) {
    handleCartError(get(t)('cart.errors.could-not-update'), error);
  }
};

const handleRemoveFreeGift = async (cart: CompleteCartFragment | null | undefined) => {
  try {
    // no cart
    if (!cart) return null;

    const $tieredCampaign = get(tieredCampaign);
    const $freeGiftCampaign = get(freeGiftCampaign);

    const currentLevel =
      cart.lines.nodes.reduce((acc, line) => {
        const filter = $tieredCampaign?.tag_filter_type || 'exclude';
        if (
          filter === 'exclude' &&
          $tieredCampaign?.tagFilters.some((exclusion) =>
            line.merchandise.product.tags.includes(exclusion)
          )
        ) {
          return acc;
        }
        if (
          filter === 'include' &&
          $tieredCampaign?.tagFilters.some(
            (exclusion) => !line.merchandise.product.tags.includes(exclusion)
          )
        ) {
          return acc;
        }
        if (
          (line.attributes || [])?.some((item) => item.key === FREE_GIFT) ||
          line.cost.totalAmount.amount === '0.0'
        ) {
          return acc;
        }
        return acc + line.quantity;
      }, 0) || 0;

    const freeGiftLevel = Number($freeGiftCampaign?.show_free_gift_on_tier_level || 10);

    if (currentLevel >= freeGiftLevel) return null;

    // find the element in the cart that has the free-gift attribute
    const freeGift = cart.lines.nodes.find((line) =>
      (line.attributes || [])?.some((item) => item.key === FREE_GIFT)
    );
    if (freeGift) {
      await get(updateCartQuantity)(freeGift.id, 0, freeGift.merchandise.id);
    }
  } catch (error) {
    handleCartError(get(t)('cart.errors.could-not-update'), error);
  }
};

type UpdateGiftFunction = () => Promise<void>;

export const updateGift: Loadable<UpdateGiftFunction> = derived(
  [addToCart, updateCartQuantity, cart, selectedGift],
  ([$addToCart, $updateCartQuantity, $cart, $selectedGift]) => {
    return async () => {
      try {
        const freeProduct = $cart?.lines.nodes.find((line) =>
          (line.attributes || [])?.some((item) => item.key === FREE_GIFT)
        );

        if (
          !$selectedGift?.variantId ||
          (freeProduct && freeProduct.merchandise.id === $selectedGift?.variantId)
        ) {
          return;
        }

        if (freeProduct) {
          await $updateCartQuantity(
            freeProduct?.id as string,
            0,
            freeProduct?.merchandise?.id as string
          );
        }

        return $addToCart([
          {
            merchandiseId: `gid://shopify/ProductVariant/${$selectedGift?.variantId}`,
            quantity: 1,
            attributes: [{ key: FREE_GIFT, value: 'true' }],
            sellingPlanId: $selectedGift?.sellingPlanId
          }
        ]);
      } catch (error) {
        handleCartError(get(t)('cart.errors.could-not-update'), error);
      }
    };
  }
);

/* GraphQL */ `
  mutation createCart($lines: [CartLineInput!]!, $buyerIdentity: CartBuyerIdentityInput) {
    cartCreate(input: { lines: $lines, buyerIdentity: $buyerIdentity }) {
      cart {
        ...CompleteCart
      }
      userErrors {
        field
        message
      }
    }
  }

  mutation addToCart($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ...CompleteCart
      }
      userErrors {
        field
        message
      }
    }
  }

  mutation updateCartQuantity($cartId: ID!, $lineId: ID!, $quantity: Int!) {
    cartLinesUpdate(cartId: $cartId, lines: [{ id: $lineId, quantity: $quantity }]) {
      cart {
        ...CompleteCart
      }
      userErrors {
        field
        message
      }
    }
  }

  mutation addDiscountCode($cartId: ID!, $discountCodes: [String!]) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        ...CompleteCart
      }
      userErrors {
        field
        message
      }
    }
  }
`;
